/**
 * IT > Changelog (/it/changelog). Writes the What's new entries both Cockpit and
 * The Sweet Spot show behind the avatar menu. Gated by `it.changelog`.
 *
 * The reader page is `pages/settings/Changelog.tsx` at /changelog, open to every
 * signed-in user. This page is the authoring half: pick the target app, name the
 * tools it touched, write the note, publish. Backend api#541.
 *
 * The entry list shows BOTH apps, because an admin manages all of them, so it
 * merges the two per-app reads and dedupes by id (an entry targeting `both` is
 * returned by each).
 */

import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { RichTextEditor, gridTheadClassName } from '@ampersandbrands/ui';
import { FilterLabel } from '@/components/FilterLabel';
import { Pill } from '@/components/Pill';
import { SearchableSelect, type SearchableSelectOption } from '@/components/SearchableSelect';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { uploadEditorImage } from '@/lib/editorImages';
import { formatDateTime } from '@/lib/dateUtils';
import { useSession } from '@/lib/useSession';
import {
  CHANGELOG_APPS,
  CHANGELOG_APP_LABELS,
  changelogToolKey,
  changelogToolOptions,
  hasChangelogAdmin,
  useChangelog,
  useCreateChangelogEntry,
  useDeleteChangelogEntry,
  type ChangelogApp,
  type ChangelogEntry,
  type ChangelogToolRef,
} from '@/lib/useChangelog';

/**
 * The sentinel option meaning "the whole app". It rides inside the same picker
 * as the real tools so one control covers all three shapes Steven asked for:
 * pick one tool, pick many, or pick All. Selecting it clears any real tools and
 * selecting a real tool clears it, so the two can never both be set (which the
 * API rejects anyway).
 */
const ALL_TOOLS = '__all__';

function AccessDeniedCard() {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        You do not have access to the Changelog tool. Ask IT for the Changelog permission if you
        need to publish What&apos;s new entries.
      </p>
    </div>
  );
}

function Compose() {
  const [app, setApp] = useState<ChangelogApp>('cockpit');
  const [title, setTitle] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  // The editor's plain-text view of the same body. Emptiness is judged on
  // this, because an empty editor still emits `<p></p>` as its HTML.
  const [bodyText, setBodyText] = useState('');
  const [toolKeys, setToolKeys] = useState<string[]>([]);
  // Remounts the editor after a publish so it clears; TipTap reads
  // `initialContent` on mount only.
  const [formKey, setFormKey] = useState(0);
  const createMut = useCreateChangelogEntry();

  const toolOptions = useMemo(() => changelogToolOptions(app), [app]);

  const options: SearchableSelectOption[] = useMemo(
    () => [
      { value: ALL_TOOLS, label: 'All tools', sublabel: 'Everything in the target app' },
      ...toolOptions.map((t) => ({
        value: changelogToolKey(t),
        label: t.tool_label,
        sublabel: t.group,
      })),
    ],
    [toolOptions],
  );

  // Changing the target app can strip a tool that no longer belongs to it (a
  // Sweet Spot tool on a Cockpit-only entry), which the API rejects. Drop those
  // in the same write rather than letting the form hold an invalid selection.
  function changeApp(next: ChangelogApp) {
    setApp(next);
    const valid = new Set(changelogToolOptions(next).map(changelogToolKey));
    setToolKeys((prev) => prev.filter((k) => k === ALL_TOOLS || valid.has(k)));
  }

  function changeTools(next: string[]) {
    const addedAll = next.includes(ALL_TOOLS) && !toolKeys.includes(ALL_TOOLS);
    if (addedAll) {
      setToolKeys([ALL_TOOLS]);
      return;
    }
    setToolKeys(next.filter((k) => k !== ALL_TOOLS));
  }

  const allTools = toolKeys.includes(ALL_TOOLS);
  const selectedTools: ChangelogToolRef[] = useMemo(() => {
    if (allTools) return [];
    const byKey = new Map(toolOptions.map((t) => [changelogToolKey(t), t]));
    return toolKeys
      .map((k) => byKey.get(k))
      .filter((t): t is (typeof toolOptions)[number] => t !== undefined)
      .map(({ app: toolApp, tool_id, tool_label }) => ({ app: toolApp, tool_id, tool_label }));
  }, [allTools, toolKeys, toolOptions]);

  const bodyFilled = bodyText.trim().length > 0;
  const canPublish =
    title.trim().length > 0 && bodyFilled && (allTools || selectedTools.length > 0);

  function publish() {
    if (!canPublish) return;
    createMut.mutate(
      { app, title: title.trim(), body_html: bodyHtml, all_tools: allTools, tools: selectedTools },
      {
        onSuccess: () => {
          setTitle('');
          setBodyHtml('');
          setBodyText('');
          setToolKeys([]);
          setFormKey((n) => n + 1);
        },
      },
    );
  }

  return (
    <div className="mb-6 rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="mb-3 grid gap-3 sm:grid-cols-2">
        <div>
          <FilterLabel htmlFor="changelog-site">Site</FilterLabel>
          <select
            id="changelog-site"
            value={app}
            onChange={(e) => changeApp(e.target.value as ChangelogApp)}
            className="mt-1 w-full rounded-md border border-amp-line bg-amp-white px-2 py-1.5 text-[13px] text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            {CHANGELOG_APPS.map((a) => (
              <option key={a} value={a}>
                {CHANGELOG_APP_LABELS[a]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <FilterLabel as="div">Tools changed</FilterLabel>
          <div className="mt-1">
            <SearchableSelect
              options={options}
              selected={toolKeys}
              onChange={changeTools}
              placeholder="Pick tools"
              ariaLabel="Tools this entry covers"
              className="w-full"
            />
          </div>
          <p className="mt-1 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {app === 'both'
              ? 'Both sites selected, so every tool in Cockpit and The Sweet Spot is offered.'
              : 'Pick one, pick several, or pick All tools for a whole-site change.'}
          </p>
        </div>
      </div>

      <div className="mb-3">
        <FilterLabel htmlFor="changelog-title">Title</FilterLabel>
        <input
          id="changelog-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="What changed, in a few words"
          className="mt-1 w-full rounded-md border border-amp-line bg-amp-white px-2 py-1.5 text-[13px] text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        />
      </div>

      <div className="mb-3">
        <FilterLabel as="div">What changed</FilterLabel>
        <div className="mt-1">
          <RichTextEditor
            key={formKey}
            value={bodyHtml}
            onChange={(html, text) => {
              setBodyHtml(html);
              setBodyText(text);
            }}
            onInsertImage={uploadEditorImage}
            aria-label="What changed"
            className="min-h-[16rem]"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={publish}
          disabled={!canPublish || createMut.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-amp-olive-deep px-3 py-1.5 text-[13px] font-semibold text-amp-cream disabled:opacity-60 dark:bg-amp-olive"
        >
          {createMut.isPending && <Loader2 size={14} className="animate-spin" aria-hidden />}
          Publish
        </button>
        <span className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Publishing shows the entry immediately to everyone on the selected site.
        </span>
      </div>
      {createMut.error && (
        <p className="mt-2 text-[13px] text-red-700 dark:text-red-300">
          Could not publish: {createMut.error.message}
        </p>
      )}
    </div>
  );
}

function ToolCells({ entry }: { entry: ChangelogEntry }) {
  if (entry.all_tools) {
    return <Pill variant="outline">All tools</Pill>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {entry.tools.map((t) => (
        <Pill key={changelogToolKey(t)} variant="outline">
          {t.tool_label}
        </Pill>
      ))}
    </div>
  );
}

function EntryRow({ entry }: { entry: ChangelogEntry }) {
  const [confirming, setConfirming] = useState(false);
  const deleteMut = useDeleteChangelogEntry();

  return (
    <tr>
      <td className="px-3 py-2 align-top text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {formatDateTime(entry.published_at)}
      </td>
      <td className="px-3 py-2 align-top">
        <Pill variant="outline">{CHANGELOG_APP_LABELS[entry.app] ?? entry.app}</Pill>
      </td>
      <td className="px-3 py-2 align-top font-semibold text-amp-ink dark:text-amp-dark-ink">
        {entry.title}
      </td>
      <td className="px-3 py-2 align-top">
        <ToolCells entry={entry} />
      </td>
      <td className="px-3 py-2 align-top text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {entry.author_email}
      </td>
      <td className="px-3 py-2 align-top">
        {confirming ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => deleteMut.mutate(entry.id, { onSuccess: () => setConfirming(false) })}
              disabled={deleteMut.isPending}
              className="rounded px-2 py-0.5 text-[12px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60 dark:text-red-300 dark:hover:bg-red-900/20"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded px-2 py-0.5 text-[12px] text-amp-ink-muted hover:bg-amp-cream-warm dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface-warm"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            aria-label={`Delete ${entry.title}`}
            className="rounded px-2 py-0.5 text-[12px] text-amp-ink-muted hover:bg-amp-cream-warm hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface-warm dark:hover:text-amp-dark-ink"
          >
            Delete
          </button>
        )}
      </td>
    </tr>
  );
}

export function Changelog() {
  const { user } = useSession();
  const canView = hasChangelogAdmin(user?.permissions, user?.superAdmin);

  // Both feeds, so the admin sees every entry. A `both` entry comes back from
  // each, so they are deduped by id.
  const cockpitQ = useChangelog('cockpit');
  const sweetspotQ = useChangelog('sweetspot');

  const rows = useMemo(() => {
    const byId = new Map<number, ChangelogEntry>();
    for (const e of cockpitQ.data?.entries ?? []) byId.set(e.id, e);
    for (const e of sweetspotQ.data?.entries ?? []) byId.set(e.id, e);
    return [...byId.values()].sort((a, b) => b.published_at.localeCompare(a.published_at));
  }, [cockpitQ.data, sweetspotQ.data]);

  const isLoading = cockpitQ.isLoading || sweetspotQ.isLoading;
  const error = cockpitQ.error ?? sweetspotQ.error;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
            IT - Changelog
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Changelog</h1>
          <p className="mt-1 max-w-2xl text-sm text-amp-ink-muted line-clamp-2 dark:text-amp-dark-ink-muted md:line-clamp-none">
            Write the What&apos;s new notes users read in Cockpit and The Sweet Spot. Pick the site,
            name the tools that changed, and publish. Entries appear behind the avatar menu.
          </p>
        </div>
        <ToolPinToggle toolSlug="changelog" toolName="Changelog" />
      </div>

      {user && !canView ? (
        <AccessDeniedCard />
      ) : (
        <>
          <Compose />
          <DataGridShell total={rows.length} loaded={rows.length} countNoun="entries">
            <table className="min-w-full text-sm">
              <thead className={gridTheadClassName}>
                <tr>
                  <th className="w-[170px] px-3 py-2 text-left font-semibold">Published</th>
                  <th className="w-[110px] px-3 py-2 text-left font-semibold">Site</th>
                  <th className="px-3 py-2 text-left font-semibold">Title</th>
                  <th className="px-3 py-2 text-left font-semibold">Tools</th>
                  <th className="px-3 py-2 text-left font-semibold">Author</th>
                  <th className="w-[130px] px-3 py-2 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted"
                    >
                      Loading entries...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted"
                    >
                      The changelog is not available yet. It appears once the backend is deployed.
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted"
                    >
                      No entries yet. Publish the first one above.
                    </td>
                  </tr>
                ) : (
                  rows.map((e) => <EntryRow key={e.id} entry={e} />)
                )}
              </tbody>
            </table>
          </DataGridShell>
        </>
      )}
    </div>
  );
}
